import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Divider,
  IconButton,
  Toolbar,
  Typography,
  Button,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  CalendarMonth,
  Assessment,
  ChevronLeft,
  Menu as MenuIcon,
  People,
  FolderOpen,
  Dashboard,
  Logout,
  AccountTree,
  Group,
  Security,
  Euro,
  Info,
  TrendingUp,
  Work,
  Psychology,
  AdminPanelSettings,
  CloudSync,
  Folder,
  SpaceDashboard,
  TableChart,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 260;

interface MenuItem {
  title: string;
  icon: React.ReactElement;
  path?: string;
  children?: { title: string; path: string; icon: React.ReactElement; requiredPermission?: { entita: string; azione: 'create' | 'read' | 'update' | 'delete' } }[];
  requiredPermission?: { entita: string; azione: 'create' | 'read' | 'update' | 'delete' };
}

const menuItems: MenuItem[] = [
  {
    title: 'Pianificazione',
    icon: <CalendarMonth />,
    children: [
      { title: 'Progetta', path: '/pianificazione', icon: <CalendarMonth fontSize="small" /> },
      { title: 'Gruppi Progetti', path: '/pianificazione/gruppi-progetti', icon: <Folder fontSize="small" />, requiredPermission: { entita: 'progetti', azione: 'read' } },
      { title: 'Vista Piano', path: '/pianificazione/vista', icon: <TableChart fontSize="small" /> },
    ],
  },
  {
    title: 'PM Dashboard',
    icon: <SpaceDashboard />,
    children: [
      { title: 'I miei progetti', path: '/pm-dashboard', icon: <SpaceDashboard fontSize="small" /> },
    ],
  },
  {
    title: 'Analytics',
    icon: <Assessment />,
    children: [
      { title: 'Dashboard', path: '/', icon: <Dashboard fontSize="small" /> },
      { title: 'Economics', path: '/reportistica/economics', icon: <Euro fontSize="small" /> },
      { title: 'Capacity Planning', path: '/reportistica/capacity-planning', icon: <TrendingUp fontSize="small" /> },
      { title: 'Capacity Planning 2', path: '/reportistica/capacity-planning-v2', icon: <Assessment fontSize="small" /> },
      { title: 'Investimenti', path: '/analytics/investimenti', icon: <Euro fontSize="small" /> },
    ],
  },
  {
    title: 'Admin',
    icon: <AdminPanelSettings />,
    children: [
      { title: 'Sync Clienti', path: '/admin/sync-clienti', icon: <CloudSync fontSize="small" />, requiredPermission: { entita: 'progetti', azione: 'create' } },
      { title: 'Sync Progetti', path: '/admin/sync-progetti', icon: <CloudSync fontSize="small" />, requiredPermission: { entita: 'progetti', azione: 'create' } },
      { title: 'Sync Rendicontazioni', path: '/admin/sync-rendicontazioni', icon: <TrendingUp fontSize="small" />, requiredPermission: { entita: 'progetti', azione: 'create' } },
      { title: 'Progetti', path: '/anagrafiche/progetti', icon: <FolderOpen fontSize="small" />, requiredPermission: { entita: 'progetti', azione: 'read' } },
      { title: 'Risorse', path: '/anagrafiche/risorse', icon: <People fontSize="small" />, requiredPermission: { entita: 'risorse', azione: 'read' } },
      { title: 'Figure Professionali', path: '/anagrafiche/figure-professionali', icon: <Work fontSize="small" />, requiredPermission: { entita: 'risorse', azione: 'read' } },
      { title: 'Skills', path: '/anagrafiche/skills', icon: <Psychology fontSize="small" />, requiredPermission: { entita: 'risorse', azione: 'read' } },
      { title: 'Strutture', path: '/anagrafiche/strutture', icon: <AccountTree fontSize="small" />, requiredPermission: { entita: 'strutture', azione: 'read' } },
      { title: 'Utenti', path: '/anagrafiche/utenti', icon: <Group fontSize="small" />, requiredPermission: { entita: 'utenti', azione: 'read' } },
      { title: 'Permessi', path: '/anagrafiche/permessi', icon: <Security fontSize="small" />, requiredPermission: { entita: 'utenti', azione: 'update' } },
    ],
  },
  {
    title: 'Info',
    icon: <Info />,
    children: [
      { title: 'Informazioni', path: '/info', icon: <Info fontSize="small" /> },
    ],
  },
];

const Sidebar: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['Reportistica']));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleSection = (title: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(title)) {
      newExpanded.delete(title);
    } else {
      newExpanded.add(title);
    }
    setExpandedSections(newExpanded);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Filtra le voci di menu in base ai permessi dell'utente
  const filteredMenuItems = menuItems.map(section => {
    if (!section.children) return section;

    const filteredChildren = section.children.filter(child => {
      if (!child.requiredPermission) return true; // Nessun permesso richiesto, mostra sempre
      return hasPermission(child.requiredPermission.entita, child.requiredPermission.azione);
    });

    return { ...section, children: filteredChildren };
  }).filter(section => !section.children || section.children.length > 0); // Rimuovi sezioni vuote

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? drawerWidth : 60,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : 60,
          boxSizing: 'border-box',
          transition: 'width 0.3s',
          overflowX: 'hidden',
        },
      }}
    >
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          {open && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="img" src="/logo-myplanner.png" alt="HarpaPlanner" sx={{ width: 32, height: 32 }} />
              <Typography variant="h6" noWrap component="div">
                HarpaPlanner
              </Typography>
            </Box>
          )}
          <IconButton onClick={toggleDrawer}>
            {open ? <ChevronLeft /> : <MenuIcon />}
          </IconButton>
        </Box>
      </Toolbar>
      <Divider />

      <List>
        {filteredMenuItems.map((item) => (
          <React.Fragment key={item.title}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => item.children && item.children.length > 0 && toggleSection(item.title)}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                {open && (
                  <>
                    <ListItemText primary={item.title} />
                    {item.children && item.children.length > 0 && (
                      expandedSections.has(item.title) ? <ExpandLess /> : <ExpandMore />
                    )}
                  </>
                )}
              </ListItemButton>
            </ListItem>

            {item.children && item.children.length > 0 && (
              <Collapse in={expandedSections.has(item.title) && open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((child) => (
                    <ListItemButton
                      key={child.path}
                      sx={{
                        pl: 4,
                        bgcolor: location.pathname === child.path ? 'action.selected' : 'transparent',
                      }}
                      onClick={() => handleNavigation(child.path)}
                    >
                      <ListItemIcon>{child.icon}</ListItemIcon>
                      <ListItemText primary={child.title} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>

      {/* User info and logout */}
      <Box sx={{ position: 'absolute', bottom: 0, width: '100%', p: 2 }}>
        <Divider sx={{ mb: 2 }} />
        {open && user && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {user.nome} {user.cognome}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {user.username}
            </Typography>
          </Box>
        )}
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<Logout />}
          onClick={handleLogout}
          sx={{ textTransform: 'none' }}
        >
          {open ? 'Esci' : ''}
        </Button>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
